import {useEffect,useState} from 'react';
import { useSelector,useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { 
  Button,
  Group,
  Anchor,
  Box ,
  List,
  Mark,
  Stack,
  Popover,
  Text,
  Title,
  Collapse,
  Grid,
  Card,
  ThemeIcon,
  SegmentedControl
} from '@mantine/core';
import { useMediaQuery,useDisclosure } from '@mantine/hooks';
import { IconEdit, IconAnalyze, IconFolder, IconCircleCheck, IconUpload, IconInfoCircle, IconPlus, IconHelpCircle, IconClick } from '@tabler/icons-react';
import { loadMatrixPei } from '../actions/peiActions';
import { loadEvidences } from '../actions/evidencesActions';
import StrategicPlanDiagram from '../components/Evidences/StrategicPlanDiagram';
import DashboardEvidences from '../components/Evidences/DashboardEvidences';
import ListedEvidences from '../components/Evidences/ListedEvidences';
import useActiveProcess from '../hooks/useActiveProcess';
import { useActivePermission } from '../hooks/useActivePermission';
import useFeatureAccess from '../hooks/useFeatureAccess';
import rubricaURL from '../assets/Rúbrica 1.pdf';

const EvidencesPage = () => {
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const activeProcess = useActiveProcess();
  const activePermission = useActivePermission();
  const { canEnter, canEdit, readOnly, reason } = useFeatureAccess('evidences');   // { canEnter, canEdit, readOnly, reason }

  const {user} = useSelector(state => state.auth);

  const [guideOpened, setGuideOpened] = useState(false);
  const [popover1Opened, setPopover1Opened] = useState(false);
  const [popover2Opened, setPopover2Opened] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const isMobile = useMediaQuery('(max-width: 50em)');

  useEffect(() => {
    if(canEnter){
      if (activeProcess?.Id && activePermission) {
        handleFetchEvidences();
        dispatch(loadMatrixPei(activeProcess.matriz.Id, user));
      }
    }else{
      navigate('/backoffice')
    }
  }, [activeProcess?.Id, activePermission]);

  const handleFetchEvidences = () => {
    const unidadGestion = activePermission.permiso?.EntityId || '';
    const sede = activePermission.sede?.codigo || '';
    
    dispatch(loadEvidences(null, activeProcess.Id, user, unidadGestion, sede));
  }

  const handleDownloadRubrica = () => {
    const link = document.createElement('a');
    link.href = rubricaURL;
    link.download = 'Rúbrica-KPI.pdf';
    link.click();
  };

  const estrategias = [
    {
      icon: <ThemeIcon variant="light" color="#e72166" radius="xl"><IconAnalyze height="70%" width="70%" /></ThemeIcon>,
      title: "Análisis de cada KPI",
      content: (
        <>
          <Mark color="rgba(231, 33, 102, 0.25)">Analice los 33 KPI</Mark> para comprender los parámetros que se están evaluando.
        </>
      ),
    },
    {
      icon: <ThemeIcon variant="light" color="#e72166" radius="xl"><IconEdit height="70%" width="70%" /></ThemeIcon>,
      title: "Confección de Evidencias",
      content: (
        <>
          Elabore <Mark color="rgba(231, 33, 102, 0.25)">hasta 2 evidencias por KPI</Mark>, priorizando aquellas que demuestren un impacto directo y significativo.

          <Popover width={200} position="bottom" withArrow shadow="md" opened={popover1Opened}>
            <Popover.Target>
              <IconHelpCircle style={{marginLeft:'5px'}} size={18} onMouseEnter={() => setPopover1Opened(true)} onMouseLeave={() => setPopover1Opened(false)}/> 
            </Popover.Target>
            <Popover.Dropdown style={{ pointerEvents: 'none' }}>
              <Text size="sm">Podrá encontrar ejemplos de evidencias al ingresar a cada KPI.</Text>
            </Popover.Dropdown>
          </Popover>
        </>
      ),
    },
    {
      icon: <ThemeIcon variant="light" color="#e72166" radius="xl"><IconFolder height="70%" width="70%" /></ThemeIcon>,
      title: "Consolidación de Evidencias",
      content: (
        <>
          Integre toda la información de <Mark color="rgba(231, 33, 102, 0.25)">cada evidencia en un único archivo en formato PDF</Mark>, con un <Mark color="rgba(231, 33, 102, 0.25)">peso máximo de 10 MB</Mark>. Asegúrese que cada archivo incluya una descripción exhaustiva con datos cuantitativos y cualitativos, además de fotos, imágenes, tablas y gráficos, según corresponda. No incluya enlaces externos en el documento, dado que comprometen la persistencia y la integridad de los datos.
          
          <Popover width={500} position="bottom" withArrow shadow="md" opened={popover2Opened}>
            <Popover.Target>
              <IconHelpCircle style={{marginLeft:'5px'}} size={18} onMouseEnter={() => setPopover2Opened(true)} onMouseLeave={() => setPopover2Opened(false)}/> 
            </Popover.Target>
            <Popover.Dropdown style={{ pointerEvents: 'none' }}>
              <Text size="sm">Un enlace externo se refiere a cualquier hipervínculo que dirija a recursos o páginas fuera del documento principal, tales como sitios web, repositorios en línea o bases de datos digitales. La inclusión de estos enlaces puede resultar en problemas de persistencia de la información, ya que el contenido al que se hace referencia puede cambiar o incluso dejar de estar disponible con el tiempo, comprometiendo así la verificabilidad y durabilidad de las evidencias presentadas. </Text>
            </Popover.Dropdown>
          </Popover>
        </>
      ),
    },
    {
      icon: <ThemeIcon variant="light" color="#e72166" radius="xl"><IconCircleCheck height="70%" width="70%" /></ThemeIcon>,
      title: "Verificación de Evidencias",
      content: (
        <>
          Asegúrese que la evidencia presentada consista únicamente en <Mark color="rgba(231, 33, 102, 0.25)">datos verificables y relevantes</Mark>. Evite incluir opiniones, interpretaciones subjetivas o sugerencias de mejoras en esta etapa del proceso.
        </>
      ),
    },
    {
      icon: <ThemeIcon variant="light" color="#e72166" radius="xl"><IconUpload height="70%" width="70%" /></ThemeIcon>,
      title: "Carga de Evidencias",
      content: (
        <>
          Suba cada archivo consolidado seleccionando el <Mark color="rgba(231, 33, 102, 0.25)">botón +</Mark> en el KPI correspondiente. Puede subir hasta 2 evidencias por cada KPI.
        </>
      ),
    },
  ];

  const renderTab = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <DashboardEvidences/>
        );
      case 'list':
        return (
          <ListedEvidences
            editEvidenceEnabled={canEdit}
          />
        );
      /*case 'graphs':
        return <PlanesEstrategicos planes={planesEstrategicos} />;*/
      default:
        return null;
    }
  };

  return (
    <div>
      <section className='bienvenida'>
        <div className='bienvenida-header'>
          <Group justify={'space-between'}>
            <Box >
              <Title fw={400} order={1} >Gestión de Evidencias</Title>
              <Text size='lg'>Visualiza, filtra y analiza las evidencias de tu unidad de gestión</Text>
            </Box>
            <Group>
              <Button variant='default' leftSection={<IconInfoCircle size={18}/>} onClick={()=> setGuideOpened(!guideOpened)}>
                {guideOpened ? 'Ocultar' : 'Mostrar'} ayuda
              </Button>
              {canEdit && (
                <Button color='#e72166' leftSection={<IconPlus size={18}/>} onClick={() => navigate('publish')}>Nueva Evidencia</Button>
              )}
            </Group>
          </Group>
        </div>
        <Collapse in={guideOpened} transitionDuration={500}>
          <Card withBorder shadow='sm' radius={'md'} p={'lg'} style={{borderLeftColor: '#e72166', borderLeftWidth: '4px'}}>
            <Text size='sm' mb={'xl'}>
              Cada KPI está diseñado para evaluar el rendimiento de los criterios de calidad previstos en el sistema de monitoreo del Plan Estratégico Institucional. 
            </Text>
            <Grid gutter={'md'}>
              <Grid.Col span={{base:12, md:8.5}}>
                <Stack gap="md">

                  <Text size="sm" fw={500}>
                    Estrategias sugeridas para la elaboración de cada evidencia y para su registro en la plataforma
                  </Text>

                  <List spacing="xs" withPadding>
                    {estrategias.map((item, idx) => (
                      <List.Item key={idx} icon={item.icon}>
                        <>
                          <Text size='xs' fw={500}>{item.title}</Text>
                          <Text size='xs' c={'gray.9'}>{item.content}</Text> 
                        </>
                      </List.Item>
                    ))}
                  </List>

                  <Text size="sm" mt={'md'}> 
                    Utilice la <Button color={'#e72166'} variant='subtle' onClick={()=>handleDownloadRubrica()} leftSection={<IconClick size={18}/>}>Rúbrica</Button> para evaluar y ajustar cada una de sus evidencias, en caso de ser necesario.
                  </Text>

                </Stack>
              </Grid.Col>
              <Grid.Col span={{base:12, md:3.5}}>
                <StrategicPlanDiagram/>
              </Grid.Col>
            </Grid>
          </Card>
        </Collapse>
      </section>
      <section>
        <Group mb={'lg'}>
          <SegmentedControl
            value={activeTab}
            onChange={setActiveTab}
            withItemsBorders={false}
            data={[
              { label: 'Dashboard', value: 'dashboard' },
              { label: 'Listado de Evidencias', value: 'list' },
              { label: 'Graficos', value: 'graphs', disabled:true },
            ]}
          />
        </Group>
        <Box p={'15px 0px'}>
          {renderTab(activeTab)}
        </Box>
      </section>
    </div>
  );
};

export default EvidencesPage;